import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, getDocs, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { isPairApproved, restoreApplicationsForPost } from "./affiliationService";
import { color, font } from "./theme";

const MAPBOX_TOKEN = "pk.eyJ1IjoibWFraWlpaS0iLCJhIjoiY21wbTgybHVmMmc1ZzJycTFuZXRlb3NoNCJ9.FIpjF2lKTHkbU1e6qrL_Pw";
// Itim na ang brand color ng screen na ito, galing sa theme (blush100 =
// #161616) — ito ang kapalit ng dating #590101, tulad ng ginawa sa
// Company/Coordinator dashboards.
// Ang darkRed/red sa ibaba ay para na lang sa mga SEMANTIC na pula: error at
// warning banners, "Expired" badges, at ang Delete/danger confirm. Hindi 'yon
// branding kaya hindi dapat maging itim.
const inkDeep = color.blush100;
const darkRed = "#590101";
const red     = "#8B0000";

// ── Responsive styles ─────────────────────────────────────────────────────────
const ResponsiveStyles = () => (
  <style>{`
    /* Post grid: 2-col on ≥640px, 1-col below */
    .post-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    @media (max-width: 639px) {
      .post-grid { grid-template-columns: 1fr; }
    }

    /* Post modal */
    .post-modal-inner {
      width: 640px;
      max-width: 100%;
    }
    @media (max-width: 680px) {
      .post-modal-inner {
        width: 100%;
        max-width: 460px;
        border-radius: 14px;
        max-height: 82vh;
      }
    }
    @media (max-width: 480px) {
      .post-modal-inner {
        max-height: 80dvh;
      }
    }
    /* Mobile: same container size as CoordinatorReportCompanyScreen's report modal.
       !important because the container carries inline maxHeight / borderRadius. */
    @media (max-width: 560px) {
      .post-modal-inner {
        width: calc(100vw - 72px) !important;
        max-width: none !important;
        max-height: 68vh !important;
        border-radius: 14px !important;
      }
    }

    /* Modal body padding */
    .post-modal-body {
      padding: 0 28px 16px;
    }
    @media (max-width: 480px) {
      .post-modal-body { padding: 0 14px 12px; }
    }

    /* Modal header padding */
    .post-modal-header {
      padding: 20px 28px 12px;
    }
    @media (max-width: 480px) {
      .post-modal-header { padding: 14px 14px 10px; }
    }

    /* Modal footer padding */
    .post-modal-footer {
      padding: 14px 28px;
    }
    @media (max-width: 480px) {
      .post-modal-footer { padding: 12px 14px; }
    }

    /* Description + map row: stacked vertically, map full width */
    .post-desc-row {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    /* Map box: full width, tall */
    .post-map-box {
      width: 100%;
      height: 320px;
      flex-shrink: 0;
      border-radius: 16px;
      overflow: hidden;
    }
    @media (max-width: 480px) {
      .post-map-box { height: 220px; }
    }

    /* Working hours + slot row: side-by-side, but slot drops below on very small screens */
    .post-hours-slot-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
    @media (max-width: 400px) {
      .post-hours-slot-row { flex-direction: column; }
      .post-slot-col { flex-direction: row; align-items: center; gap: 10px; }
    }

    /* Contact info: label + input row */
    .post-contact-row {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    @media (max-width: 400px) {
      .post-contact-row { flex-direction: column; gap: 4px; }
    }

    /* Screen padding */
    .post-screen-padding {
      padding: 28px 32px;
    }
    @media (max-width: 640px) {
      .post-screen-padding { padding: 16px 16px; }
    }

    /* Recent Post header row */
    .post-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    /* Post button */
    .post-btn {
      padding: 10px 24px;
      font-size: 1rem;
      font-weight: 600;
    }
    @media (max-width: 480px) {
      .post-btn { padding: 8px 16px; font-size: 0.9rem; }
    }

    /* Modal overlay scroll on small screens */
    .post-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.45);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000;
      padding: 12px;
      overflow-y: auto;
    }
    @media (max-width: 480px) {
      .post-modal-overlay { padding: 10px; }
    }

    /* Confirm / success mini-modals: always centered, never pinned to top */
    .post-confirm-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.45);
      display: flex; align-items: center; justify-content: center;
      z-index: 1100;
      padding: 16px;
      overflow-y: auto;
    }
  `}</style>
);

// ── Google Fonts ──────────────────────────────────────────────────────────────
const GlobalFonts = () => {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Jersey+25&family=Jua&family=Kufam:wght@400;600&display=swap";
    document.head.appendChild(link);
    const style = document.createElement("style");
    style.id = "ojt-font-override";
    style.textContent = `
      .ojt-field { font-family: ${font.ui} !important; }
      .ojt-field::placeholder { font-family: ${font.ui} !important; color: #aaa; }
      .ojt-textarea::-webkit-scrollbar { width: 6px; }
      .ojt-textarea::-webkit-scrollbar-track { background: transparent; border-radius: 10px; }
      .ojt-textarea::-webkit-scrollbar-thumb { background: ${inkDeep}; border-radius: 10px; }
      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        opacity: 1; background: ${inkDeep}; cursor: pointer;
      }
      input[type=number] { accent-color: ${inkDeep}; }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
      const s = document.getElementById("ojt-font-override");
      if (s && document.head.contains(s)) document.head.removeChild(s);
    };
  }, []);
  return null;
};

// ── Shared pill-style field styles ────────────────────────────────────────────
const pillInputStyle = {
  width: "100%",
  padding: "8px 14px",
  background: "white",
  border: "none",
  borderRadius: "20px",
  color: "#1a1a1a",
  fontSize: "0.82rem",
  fontFamily: font.ui,
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
};

const pillInputReadonly = {
  ...pillInputStyle,
  background: "#e8e8e8",
  color: "#555",
  cursor: "default",
};

const pillTextareaStyle = {
  ...pillInputStyle,
  borderRadius: "16px",
  resize: "none",
  minHeight: "72px",
  lineHeight: 1.6,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${inkDeep} transparent`,
};

const pillTextareaReadonly = {
  ...pillTextareaStyle,
  background: "#e8e8e8",
  color: "#555",
  cursor: "default",
};

const pillSelectStyle = {
  ...pillInputStyle,
  appearance: "none",
  WebkitAppearance: "none",
  paddingRight: "34px",
  cursor: "pointer",
};

const pillSelectReadonly = {
  ...pillSelectStyle,
  background: "#e8e8e8",
  color: "#555",
  cursor: "default",
};

// ── Field Label ───────────────────────────────────────────────────────────────
const FieldLabel = ({ children }) => (
  <p style={{
    fontFamily: font.ui, fontWeight: 600,
    fontSize: "clamp(0.95rem, 2.6vw, 1.05rem)",
    color: "black",
    marginBottom: "5px",
    letterSpacing: "-0.005em",
    marginTop: "10px",
  }}>{children}</p>
);

// ── Inline sub-label ──────────────────────────────────────────────────────────
const inlineLabelStyle = {
  fontFamily: font.ui,
  fontSize: "clamp(0.85rem, 2.5vw, 1rem)",
  color: inkDeep,
  whiteSpace: "nowrap",
};

// ── Field Error ───────────────────────────────────────────────────────────────
const FieldError = ({ msg }) => msg
  ? <p style={{ fontFamily: font.ui, fontSize: "0.72rem", color: "#c00", marginTop: "3px", paddingLeft: "6px" }}>{msg}</p>
  : null;

// ── Pill Select with arrow ────────────────────────────────────────────────────
// ── CustomSelect ──────────────────────────────────────────────────────────────
// Drop-in replacement for a native <select> whose option list is drawn by us
// instead of the browser/OS. The native list (the big grey box that spilled
// past the modal) can't be styled or kept inside a container; this one:
//   • looks like the dropdown in the student "Apply Now" modal — white card,
//     12px corners, soft shadow, roomy rows, hover + selected tints;
//   • stays INSIDE the container it's in (modal body, card, page): it opens
//     downward or upward, whichever side has more room inside the nearest
//     clipping/scrolling ancestor, and caps its height to that room, so it
//     never overlaps past the container's edge;
//   • keeps the trigger looking exactly like the field it replaces — pass the
//     old <select>'s style as `triggerStyle`.
//
// Props:
//   value, onChange(value)  — same as a controlled <select> (string values)
//   options                 — strings, or { value, label } objects
//   placeholder             — text shown when nothing is picked; also listed
//                             as the first row so the choice can be cleared
//   showPlaceholderOption   — set false to leave that first row out
//   disabled, hasError      — hasError swaps the border to `errorColor`
//   triggerStyle            — style for the closed field (the old select's)
//   ariaLabel               — accessible name when there's no <label>

const CS_MAX_LIST_HEIGHT = 240;
const CS_MIN_LIST_HEIGHT = 96;
const CS_EDGE_GAP        = 8;

const csNormalize = (o) =>
  o !== null && typeof o === "object"
    ? { value: String(o.value ?? ""), label: String(o.label ?? o.value ?? "") }
    : { value: String(o ?? ""), label: String(o ?? "") };

// Nearest ancestor that clips its content (a scrolling modal body, a card
// with overflow hidden…). The list has to fit inside it.
const csFindClipParent = (el) => {
  for (let p = el?.parentElement; p && p !== document.body; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === "auto" || oy === "scroll" || oy === "hidden" || oy === "clip") return p;
  }
  return null;
};

function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  showPlaceholderOption = true,
  disabled = false,
  hasError = false,
  errorColor = color.danger,
  triggerStyle = {},
  ariaLabel,
}) {
  const [open, setOpen]         = useState(false);
  const [openUp, setOpenUp]     = useState(false);
  const [maxHeight, setMaxH]    = useState(CS_MAX_LIST_HEIGHT);
  const [active, setActive]     = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const items = [
    ...(showPlaceholderOption ? [{ value: "", label: placeholder, isPlaceholder: true }] : []),
    ...options.map(csNormalize),
  ];
  const current  = String(value ?? "");
  const selected = items.find(i => !i.isPlaceholder && i.value === current);

  // Close on any press outside the field + list.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // When it opens: pick the side with more room inside the container, cap the
  // height to that room, and bring the current choice into view.
  React.useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const rect   = wrapRef.current.getBoundingClientRect();
    const clip   = csFindClipParent(wrapRef.current);
    const bounds = clip ? clip.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const top    = Math.max(bounds.top, 0);
    const bottom = Math.min(bounds.bottom, window.innerHeight);
    const below  = bottom - rect.bottom - CS_EDGE_GAP;
    const above  = rect.top - top - CS_EDGE_GAP;
    const wanted = Math.min(CS_MAX_LIST_HEIGHT, items.length * 36 + 8);
    const up     = below < wanted && above > below;
    setOpenUp(up);
    setMaxH(Math.max(CS_MIN_LIST_HEIGHT, Math.min(CS_MAX_LIST_HEIGHT, up ? above : below)));
    const idx = items.findIndex(i => i.value === current);
    setActive(idx >= 0 ? idx : 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the keyboard-highlighted row visible while arrowing through.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    listRef.current.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const choose = (item) => {
    onChange?.(item.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Escape") { if (open) { e.stopPropagation(); setOpen(false); } return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive(a => {
        const n = items.length;
        return e.key === "ArrowDown" ? (a + 1) % n : (a - 1 + n) % n;
      });
      return;
    }
    if ((e.key === "Enter" || e.key === " ") && open && active >= 0) {
      e.preventDefault();
      choose(items[active]);
    }
  };

  const baseTrigger = {
    width: "100%",
    textAlign: "left",
    cursor: disabled ? "not-allowed" : "pointer",
    outline: "none",
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    // leave room for the caret on the right
    paddingRight: "32px",
  };
  const merged = { ...baseTrigger, ...triggerStyle };
  if (!merged.paddingRight || parseInt(merged.paddingRight, 10) < 28) merged.paddingRight = "32px";
  if (hasError) merged.borderColor = errorColor;
  if (!selected) merged.color = color.inkFaint;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        style={merged}
      >
        {selected ? selected.label : placeholder}
      </button>

      <span
        aria-hidden="true"
        style={{
          position: "absolute", right: "12px", top: "50%",
          transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
          transition: "transform 160ms ease",
          pointerEvents: "none", display: "flex",
          color: disabled ? color.inkFaint : color.inkMuted,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5z" /></svg>
      </span>

      {open && !disabled && (
        <div
          ref={listRef}
          role="listbox"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            [openUp ? "bottom" : "top"]: "calc(100% + 4px)",
            background: color.white,
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            zIndex: 2000,
            padding: "4px 0",
          }}
        >
          {items.map((item, i) => {
            const isSel = !item.isPlaceholder && item.value === current;
            const bg = i === active ? color.hoverWash : isSel ? color.wine800 : color.white;
            return (
              <div
                key={`${item.value}-${i}`}
                role="option"
                aria-selected={isSel}
                onMouseDown={(e) => e.preventDefault()}   // keep focus on the field
                onClick={() => choose(item)}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: "8px 14px",
                  fontFamily: font.ui,
                  fontSize: "0.82rem",
                  lineHeight: 1.4,
                  cursor: "pointer",
                  background: bg,
                  color: item.isPlaceholder ? color.inkFaint : color.ink,
                  fontWeight: isSel ? 600 : 400,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {item.label}
              </div>
            );
          })}
          {items.length === 0 && (
            <div style={{ padding: "10px 14px", fontFamily: font.ui, fontSize: "0.8rem", color: color.inkFaint }}>
              No options
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Custom dropdown (CustomSelect above) — same look as the student Apply Now
// dropdown, and its list stays inside the form instead of the native option
// box spilling past the modal. The closed field keeps its old pill look.
const PillSelect = ({ value, onChange, options, placeholder, disabled, hasError }) => (
  <CustomSelect
    value={value}
    onChange={onChange}
    options={options}
    placeholder={placeholder || "Select..."}
    disabled={disabled}
    hasError={hasError}
    errorColor="#c00"
    triggerStyle={{
      ...(disabled ? pillSelectReadonly : pillSelectStyle),
      border: hasError ? "1.5px solid #c00" : "none",
      color: value ? "#1a1a1a" : "#aaa",
    }}
  />
);

// ── Approved Department Picker ────────────────────────────────────────────────
// A post can only be created for the department(s)/program(s) THIS company
// has actually been approved for by the matching coordinator — not any
// college in the system (that's what the old MultiCollegeProgramPicker did,
// even though College/Program data itself now comes live from Firestore via
// useDepartmentsPrograms — see ./departmentsPrograms). Every entry in
// `approvedDeptSelections` is this company's own `deptSelections` (set at
// Sign-Up / Account Profile), pre-filtered to `status === "approved"`.
// Each checked department/program now carries its own `slot` count — a
// company posting for two departments needs to say how many OJT slots it
// has for EACH one, not one combined number for the whole post (see
// PostOJTContent.handleSave, which sums these into a `slot` total for any
// other screen that still reads that combined field).
const ApprovedDepartmentPicker = ({ approvedDeptSelections, selections, onChange, readOnly }) => {
  const findSelection = (dept, program) => selections.find(s => s.college === dept && s.program === program);
  const isChecked = (dept, program) => !!findSelection(dept, program);
  const getSlot = (dept, program) => findSelection(dept, program)?.slot ?? 1;

  const toggle = (dept, program) => {
    if (isChecked(dept, program)) {
      onChange(selections.filter(s => !(s.college === dept && s.program === program)));
    } else {
      onChange([...selections, { college: dept, program, specialization: "", slot: 1 }]);
    }
  };

  const setSlot = (dept, program, slot) => {
    onChange(selections.map(s => (s.college === dept && s.program === program) ? { ...s, slot } : s));
  };

  if (approvedDeptSelections.length === 0) {
    return (
      <div style={{ background: "#fff3f3", border: `1.5px solid ${red}`, borderRadius: "14px", padding: "12px 14px", fontFamily: font.ui, fontSize: "0.82rem", color: darkRed }}>
        You don't have any approved department yet. A coordinator needs to approve at least one of your registered departments before you can post here — check your registration status.
      </div>
    );
  }

  return (
    <div>
      {approvedDeptSelections.map((s, idx) => {
        const checked = isChecked(s.department, s.program);
        return (
          <div
            key={idx}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              background: "#ececec", borderRadius: "14px", padding: "10px 14px",
              marginBottom: "8px",
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0, cursor: readOnly ? "default" : "pointer", userSelect: "none" }}>
              <input
                type="checkbox"
                checked={checked}
                disabled={readOnly}
                onChange={() => toggle(s.department, s.program)}
                style={{ width: "17px", height: "17px", accentColor: inkDeep, cursor: readOnly ? "default" : "pointer", flexShrink: 0 }}
              />
              <span style={{ fontFamily: font.ui, fontSize: "0.85rem", color: "#222", overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 700 }}>{s.department}</span>
                {s.program && <span style={{ color: "#666" }}> — {s.program}</span>}
              </span>
            </label>
            {checked && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                <span style={{ fontFamily: font.ui, fontSize: "0.72rem", color: "#888", whiteSpace: "nowrap" }}>Slots:</span>
                <input
                  type="number"
                  min={1}
                  disabled={readOnly}
                  value={getSlot(s.department, s.program)}
                  onChange={e => setSlot(s.department, s.program, Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: "56px", textAlign: "center", padding: "5px 4px",
                    borderRadius: "10px", border: "none",
                    fontFamily: font.ui, fontSize: "0.82rem",
                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
                    background: readOnly ? "#e0e0e0" : "white",
                    color: readOnly ? "#555" : "#1a1a1a",
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Expiration date helpers ──────────────────────────────────────────────────
// Dates are stored/compared as plain "YYYY-MM-DD" strings (what <input type="date">
// gives us), so lexicographic comparison is safe and avoids timezone drift from
// building Date objects. A post is "expired" the day AFTER its expirationDate —
// i.e. if a company sets Sept 9, applying is still allowed ON Sept 9 and only
// blocked starting Sept 10.
const getTodayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isPostExpired = (post) => !!post?.expirationDate && post.expirationDate < getTodayStr();

// ── Working Hours ──────────────────────────────────────────────────────────────
// One day + one time range per entry (not a day-to-day range) — pick
// "Monday", then Monday's hours; a different day with different hours is
// its own entry via "+ Add Another Working Hours". Composed into
// "Monday (8:00am - 5:00pm)" so anything else that reads
// workingHours/workingHoursList — display screens, exports, etc. — still
// gets a single day name per entry, just no longer a "Monday - Friday" span.
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatTime12h = (t24) => {
  if (!t24) return "";
  const [hStr, mStr] = t24.split(":");
  let h = parseInt(hStr, 10);
  const period = h >= 12 ? "pm" : "am";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mStr}${period}`;
};

const to24h = (t12) => {
  const m = /^(\d{1,2}):(\d{2})(am|pm)$/i.exec((t12 || "").trim());
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const period = m[3].toLowerCase();
  if (period === "pm" && h !== 12) h += 12;
  if (period === "am" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};

// Parses a previously-saved string back into its parts, so reopening an
// existing post populates the picker instead of showing it blank. Still
// tolerates an old "Monday - Friday (...)" range saved before this change —
// it just takes the first day, since there's no day-to-day picker anymore.
const parseWorkingHours = (str) => {
  const m = /^([A-Za-z]+)(?:\s*-\s*[A-Za-z]+)?\s*\((\d{1,2}:\d{2}(?:am|pm))\s*-\s*(\d{1,2}:\d{2}(?:am|pm))\)$/i.exec((str || "").trim());
  if (!m) return { day: "", timeFrom: "", timeTo: "" };
  return { day: m[1] || "", timeFrom: to24h(m[2]), timeTo: to24h(m[3]) };
};

// ── Working Hours Input ───────────────────────────────────────────────────────
const WorkingHoursInput = ({ value, onChange, readOnly, hasError }) => {
  // Local state is the actual source of truth while editing — parsed ONCE
  // from the incoming value on mount (so reopening an existing post
  // populates correctly), never re-derived from `value` afterward. This is
  // what fixes the "won't let me type/select anything" bug: the previous
  // version re-derived every field straight from the composed string on
  // every render, and emitted `onChange("")` the instant any ONE of the
  // three parts was still missing — so the very first selection (e.g. just
  // "Day") got wiped back to blank before the person could pick the next
  // one. Now, partial progress just stays in local state; the parent only
  // hears about it once the selection is actually complete.
  const [day, setDay]           = useState(() => parseWorkingHours(value).day);
  const [timeFrom, setTimeFrom] = useState(() => parseWorkingHours(value).timeFrom);
  const [timeTo, setTimeTo]     = useState(() => parseWorkingHours(value).timeTo);

  // Re-sync from `value` whenever it changes for a reason OTHER than our
  // own onChange below (e.g. the person removes an earlier "+ Add Another
  // Working Hours" entry — since each row is keyed by array index, this
  // same component instance can end up representing a completely different
  // saved entry after the array shifts, without ever unmounting/remounting).
  // This is a safe no-op the rest of the time: after our own onChange
  // fires, `value` changes to exactly what we just emitted, and parsing it
  // back just reproduces the same three values already in state.
  useEffect(() => {
    const parsed = parseWorkingHours(value);
    setDay(parsed.day);
    setTimeFrom(parsed.timeFrom);
    setTimeTo(parsed.timeTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!day || !timeFrom || !timeTo) return; // still incomplete — don't emit yet
    const timePart = `${formatTime12h(timeFrom)} - ${formatTime12h(timeTo)}`;
    onChange(`${day} (${timePart})`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, timeFrom, timeTo]);

  const timeStyle = { ...(readOnly ? pillInputReadonly : pillInputStyle), colorScheme: "light" };

  const toLabelStyle = { fontFamily: font.ui, fontSize: "0.78rem", color: "#888", flexShrink: 0, width: "20px", textAlign: "center" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {/* Day — its own row so it never competes for width with the time inputs */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <PillSelect value={day} onChange={setDay} options={DAYS_OF_WEEK} placeholder="Day" disabled={readOnly} hasError={hasError && !day} />
        </div>
      </div>
      {/* Time range for that day — each input gets full width to breathe
          (a cramped width was clipping the native time picker's clock icon
          against the text). */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input type="time" className="ojt-field" disabled={readOnly} value={timeFrom}
            onChange={e => setTimeFrom(e.target.value)}
            style={{ ...timeStyle, width: "100%", border: hasError && !timeFrom ? "1.5px solid #c00" : "none" }} />
        </div>
        <span style={toLabelStyle}>to</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input type="time" className="ojt-field" disabled={readOnly} value={timeTo}
            onChange={e => setTimeTo(e.target.value)}
            style={{ ...timeStyle, width: "100%", border: hasError && !timeTo ? "1.5px solid #c00" : "none" }} />
        </div>
      </div>
    </div>
  );
};

// ── Phone Input ───────────────────────────────────────────────────────────────
const PhoneInput = ({ value, onChange, readOnly, hasError }) => {
  const handleChange = (e) => {
    let raw = e.target.value.replace(/\D/g, "");
    if (raw.startsWith("63")) raw = raw.slice(2);
    if (raw.length > 10) raw = raw.slice(0, 10);
    const p1 = raw.slice(0, 3), p2 = raw.slice(3, 6), p3 = raw.slice(6, 10);
    let fmt = "+63";
    if (p1) fmt += " " + p1;
    if (p2) fmt += "-" + p2;
    if (p3) fmt += "-" + p3;
    onChange(fmt);
  };
  return (
    <input className="ojt-field" type="text" disabled={readOnly}
      placeholder="+63 000-000-0000"
      value={value} onChange={handleChange}
      style={{ ...(readOnly ? pillInputReadonly : pillInputStyle), border: hasError ? "1.5px solid #c00" : "none" }} />
  );
};

// ── Gmail Email Input ─────────────────────────────────────────────────────────
const GmailInput = ({ value, onChange, readOnly, error, onBlur }) => (
  <div style={{ width: "100%" }}>
    <input className="ojt-field" type="email" disabled={readOnly}
      placeholder="example@gmail.com"
      value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur}
      style={{ ...(readOnly ? pillInputReadonly : pillInputStyle), border: error ? "1.5px solid #cc0000" : "none" }} />
    <FieldError msg={error} />
  </div>
);

// ── Mapbox Location Picker ─────────────────────────────────────────────────────
const MapboxLocationPicker = ({ value, lat, lng, onChange, readOnly }) => {
  const mapContainer = React.useRef(null);
  const mapRef       = React.useRef(null);
  const markerRef    = React.useRef(null);
  const [query, setQuery]       = useState(value || "");
  const [suggestions, setSugs]  = useState([]);
  const [geocoding, setGeocoding] = useState(false);

  // Load Mapbox GL JS dynamically
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    const link = document.createElement("link");
    link.rel  = "stylesheet";
    link.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
    script.onload = () => {
      const mapboxgl = window.mapboxgl;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/standard-satellite",
        center: lat != null && lng != null ? [lng, lat] : [120.9842, 14.5995], // default: Manila
        zoom: lat != null && lng != null ? 15 : 10,
      });
      map.addControl(new mapboxgl.NavigationControl(), "top-right");
      mapRef.current = map;

      // Prefer the company's actual stored coordinates (this may be a pin
      // they manually placed at signup) — only fall back to re-geocoding
      // the address text if we don't have coordinates at all (e.g. older
      // company records saved before pins were stored).
      if (lat != null && lng != null) {
        markerRef.current = new mapboxgl.Marker({ color: "#8B0000" }).setLngLat([lng, lat]).addTo(map);
        onChange?.({ address: value, lat, lng });
      } else if (value) {
        geocodeAndPin(value, map);
      }
    };
    document.head.appendChild(script);
  }, []);

  const geocodeAndPin = async (text, map) => {
    if (!text.trim()) return;
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(text)}.json?access_token=${MAPBOX_TOKEN}&country=PH&limit=1`
      );
      const data = await res.json();
      const feature = data.features?.[0];
      if (!feature) return;
      const [lng, lat] = feature.center;
      const m = map || mapRef.current;
      if (!m) return;
      m.flyTo({ center: [lng, lat], zoom: 15, speed: 1.4 });
      if (markerRef.current) markerRef.current.remove();
      markerRef.current = new window.mapboxgl.Marker({ color: "#8B0000" })
        .setLngLat([lng, lat])
        .addTo(m);
      onChange({ address: text, lat, lng });
    } catch (_) {}
    finally { setGeocoding(false); }
  };

  const fetchSuggestions = async (text) => {
    if (!text.trim() || text.length < 3) { setSugs([]); return; }
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(text)}.json?access_token=${MAPBOX_TOKEN}&country=PH&limit=5&types=place,locality,address,poi`
      );
      const data = await res.json();
      setSugs(data.features || []);
    } catch (_) { setSugs([]); }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    fetchSuggestions(val);
    if (!val.trim()) {
      onChange({ address: "", lat: null, lng: null });
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    }
  };

  const handleSelect = (feature) => {
    const label = feature.place_name;
    const [lng, lat] = feature.center;
    setQuery(label);
    setSugs([]);
    onChange({ address: label, lat, lng });
    const m = mapRef.current;
    if (!m) return;
    m.flyTo({ center: [lng, lat], zoom: 15, speed: 1.4 });
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = new window.mapboxgl.Marker({ color: "#8B0000" })
      .setLngLat([lng, lat])
      .addTo(m);
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Search input */}
      {!readOnly && (
        <div style={{ position: "relative", marginBottom: "8px" }}>
          <input
            type="text"
            value={query}
            onChange={handleInput}
            onKeyDown={e => { if (e.key === "Enter") { setSugs([]); geocodeAndPin(query); } }}
            placeholder="Type a location (e.g. Angeles, Pampanga)..."
            style={{ ...pillInputStyle, width: "100%", boxSizing: "border-box", paddingRight: geocoding ? "40px" : "16px" }}
          />
          {geocoding && (
            <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "0.75rem", color: "#888" }}>Searching…</span>
          )}
          {suggestions.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: "10px", zIndex: 999, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", overflow: "hidden" }}>
              {suggestions.map((s, i) => (
                <div key={i} onClick={() => handleSelect(s)}
                  style={{ padding: "9px 14px", cursor: "pointer", fontSize: "0.82rem", fontFamily: font.ui, color: "#222", borderBottom: i < suggestions.length - 1 ? "1px solid #f0f0f0" : "none" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8f0f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "white"}
                >
                  📍 {s.place_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map */}
      <div className="post-map-box">
        <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>
  );
};

// ── Post Form Modal ───────────────────────────────────────────────────────────
// ── Discard-changes confirm modal ──────────────────────────────────────────────
const ConfirmDiscardModal = ({ onKeepEditing, onDiscard }) => (
  <div className="post-confirm-overlay">
    <div style={{ background: "#fff", borderRadius: "18px", maxWidth: "360px", width: "90%", padding: "26px 22px", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", textAlign: "center" }}>
      <p style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "1.15rem", color: inkDeep, margin: "0 0 10px" }}>Discard changes?</p>
      <p style={{ fontFamily: font.ui, fontSize: "0.9rem", color: "#333", margin: "0 0 22px", lineHeight: 1.4 }}>
        All your changes will be lost. Are you sure you want to cancel this change?
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <button onClick={onKeepEditing} style={{ padding: "9px 22px", borderRadius: "22px", background: "#e6e6e6", color: "#333", border: "none", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
          Keep Editing
        </button>
        <button onClick={onDiscard} style={{ padding: "9px 22px", borderRadius: "22px", background: inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
          Discard
        </button>
      </div>
    </div>
  </div>
);

// ── Saved-successfully modal ───────────────────────────────────────────────────
const SavedSuccessModal = ({ onClose }) => (
  <div className="post-confirm-overlay">
    <div style={{ background: "#fff", borderRadius: "18px", maxWidth: "320px", width: "90%", padding: "30px 22px 24px", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", textAlign: "center" }}>
      <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#e6f7ec", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: "1.8rem", color: "#1f9254" }}>
        ✓
      </div>
      <p style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "1.15rem", color: inkDeep, margin: "0 0 6px" }}>Saved successfully!</p>
      <p style={{ fontFamily: font.ui, fontSize: "0.85rem", color: "#555", margin: "0 0 20px" }}>
        Your post has been updated.
      </p>
      <button onClick={onClose} style={{ padding: "9px 30px", borderRadius: "22px", background: inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
        OK
      </button>
    </div>
  </div>
);

// ── Generic confirm-action modal (used for Delete / Disable / Enable) ─────────
const ConfirmActionModal = ({ title, message, confirmLabel, danger = false, onCancel, onConfirm, hideCancel = false }) => (
  <div className="post-confirm-overlay">
    <div style={{ background: "#fff", borderRadius: "18px", maxWidth: "360px", width: "90%", padding: "26px 22px", boxShadow: "0 8px 40px rgba(0,0,0,0.35)", textAlign: "center" }}>
      <p style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "1.15rem", color: inkDeep, margin: "0 0 10px" }}>{title}</p>
      <p style={{ fontFamily: font.ui, fontSize: "0.9rem", color: "#333", margin: "0 0 22px", lineHeight: 1.4 }}>
        {message}
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        {!hideCancel && (
        <button onClick={onCancel} style={{ padding: "9px 22px", borderRadius: "22px", background: "#e6e6e6", color: "#333", border: "none", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
          Cancel
        </button>
        )}
        <button onClick={onConfirm} style={{ padding: "9px 22px", borderRadius: "22px", background: danger ? red : inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const PostFormModal = ({ post, mode, onClose, onSave, user, companyProfile, onModeChange }) => {
  const [isEditing, setIsEditing] = useState(mode === "create" || mode === "edit");
  // "create" | "view" | "edit" → PostOJTContent → Dashboard's "?" tour.
  useEffect(() => { onModeChange?.(mode === "create" ? "create" : isEditing ? "edit" : "view"); }, [mode, isEditing]);

  // The post's location is fixed to whatever the company has set in their
  // Account Profile — it is never typed/edited from this form. Build the
  // display address the same way PersonalInfoScreen does.
  const profileLoc = companyProfile?.location || {};
  const fixedAddress = [profileLoc.street, profileLoc.barangay, profileLoc.city, profileLoc.province, profileLoc.region]
    .filter(Boolean).join(", ");

  // Only department(s) THIS company has actually been approved for by a
  // coordinator can be posted for — see ApprovedDepartmentPicker. A new
  // post defaults to ALL currently-approved departments checked (matches
  // whatever was picked/approved at Sign-Up); an existing post keeps its
  // saved selections, but drops any entry that isn't (or is no longer)
  // approved, so a revoked approval can't linger on an old post.
  const approvedDeptSelections = (companyProfile?.deptSelections || [])
    .filter(s => s.status === "approved" && s.department);
  // Shared rule (affiliationService): tolerant of dash/spacing/case drift, and
  // a department-level approval covers every program under it.
  const isApproved = (college, program) =>
    isPairApproved(approvedDeptSelections, college, program);
  const defaultCourseSelections = approvedDeptSelections.map(s => ({ college: s.department, program: s.program, specialization: "", slot: 1 }));

  // Courses this post was created with that the company no longer accepts (or
  // that a coordinator hasn't approved). They're filtered out of the form
  // above — this names them, so an edit doesn't quietly change what the post
  // offers. The saved post keeps them until this edit is saved, and students
  // who already applied under them are untouched.
  const droppedCourseLabels = (post?.courseSelections || [])
    .filter(s => !isApproved(s.college, s.program))
    .map(s => [s.college, s.program].filter(Boolean).join(" — "));

  // The email the company registered with at Sign-Up. A NEW post starts with it
  // already filled in (still editable if they want a different contact).
  const signupEmail = companyProfile?.email || user?.email || "";

  const [form, setForm] = useState({
    benefits:         post?.benefits         || "",
    courseSelections: post?.courseSelections
      ? post.courseSelections.filter(s => isApproved(s.college, s.program))
      : defaultCourseSelections,
    skillsRequired:   post?.skillsRequired   || "",
    description:      post?.description      || "",
    requirements:     post?.requirements     || "",
    workingHoursList: post?.workingHoursList || [post?.workingHours || ""],
    phone:            post?.phone || "+63 ",
    contactEmail:     post?.contactEmail || signupEmail,
    postLocation:     post?.postLocation || { address: fixedAddress, lat: profileLoc.lat ?? null, lng: profileLoc.lng ?? null },
    expirationDate:   post?.expirationDate || "",
  });

  // Keep postLocation's address in lockstep with the company profile even if
  // the profile changes while this modal is open, or if the post predates
  // this fixed-location behavior and still has a stale/empty address.
  useEffect(() => {
    if (fixedAddress && (form.postLocation?.address !== fixedAddress || form.postLocation?.lat !== (profileLoc.lat ?? null))) {
      setForm(f => ({ ...f, postLocation: { ...f.postLocation, address: fixedAddress, lat: profileLoc.lat ?? null, lng: profileLoc.lng ?? null } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedAddress, profileLoc.lat, profileLoc.lng]);

  // The profile can finish loading after the modal opens — fill the email in
  // then, but only on a new post and only while the field is still empty.
  useEffect(() => {
    if (mode === "create" && signupEmail && !form.contactEmail) {
      setForm(f => (f.contactEmail ? f : { ...f, contactEmail: signupEmail }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupEmail]);

  const [errors, setErrors]                         = useState({});
  const [courseErrors, setCourseErrors]             = useState("");
  const [workingHoursErrors, setWorkingHoursErrors] = useState([]);
  const [dirty, setDirty]                           = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSaved, setShowSaved]                   = useState(false);
  const readOnly = !isEditing;

  const set = (field, val) => { setForm(f => ({ ...f, [field]: val })); setDirty(true); };

  const validateGmail = (val) => {
    if (!val || !val.trim()) return "Email is required.";
    return "";
  };

  const validatePhone = (val) => {
    if (!val || val.trim() === "+63" || val.trim() === "+63 ") return "Phone number is required.";
    const digits = val.replace(/\D/g, "");
    if (digits.length < 12) return "Must be a valid +63 number.";
    return "";
  };

  const handleSave = () => {
    const newErrors = {};
    if (!form.description.trim())    newErrors.description    = "Description is required.";
    if (!form.requirements.trim())   newErrors.requirements   = "Requirements is required.";
    if (!form.benefits.trim())       newErrors.benefits       = "Benefits is required.";
    if (!form.skillsRequired.trim()) newErrors.skillsRequired = "Skills Required is required.";
    if (!form.phone.trim() || form.phone.trim() === "+63") newErrors.phone = "Phone number is required.";

    const emailErr = validateGmail(form.contactEmail);
    if (emailErr) newErrors.contactEmail = emailErr;

    // Expiration date is optional (a post with none never expires), but if
    // the company sets one it can't be a date that's already passed.
    if (form.expirationDate && form.expirationDate < getTodayStr()) {
      newErrors.expirationDate = "Expiration date can't be in the past.";
    }

    const newWhErrors = form.workingHoursList.map(h => {
      if (!h.trim()) return "Please select the day(s) and time.";
      return "";
    });
    setWorkingHoursErrors(newWhErrors);
    const hasWhError = newWhErrors.some(e => e);

    // No per-entry college/program to validate anymore — every entry in
    // courseSelections is, by construction, already one of the company's
    // approved departments (see ApprovedDepartmentPicker). Just require at
    // least one to be checked.
    const hasCourseError = form.courseSelections.length === 0;
    setCourseErrors(hasCourseError ? "Select at least one department." : "");

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0 || hasWhError || hasCourseError) return;

    onSave({ ...form, workingHours: form.workingHoursList.join(", ") });
    setDirty(false);
    setShowSaved(true);
  };

  const handleCloseClick = () => {
    if (isEditing && dirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <div className="post-modal-overlay">
      <div className="post-modal-inner" style={{ background: "#d8d8d8", borderRadius: "20px", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>

        {/* Header */}
        <div className="post-modal-header" style={{ background: "#d8d8d8", flexShrink: 0 }}>
          <h2 id="cpostf-header" style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(1.1rem, 3.4vw, 1.45rem)", letterSpacing: "-0.01em", margin: 0, color: inkDeep, width: "fit-content", maxWidth: "100%" }}>
            {post?.companyName || post?.company || user?.companyName || "New Post"}
          </h2>
        </div>

        {/* Body */}
        <div className="post-modal-body" style={{ overflowY: "auto", flex: 1 }}>

          {/* Description + Requirements */}
          <div className="post-desc-row">
            <div style={{ width: "100%" }}>
              <div id="cpostf-description">
              <FieldLabel>Description:</FieldLabel>
              <textarea className="ojt-field ojt-textarea" disabled={readOnly} value={form.description} maxLength={POST_DESCRIPTION_MAX}
                onChange={e => { set("description", e.target.value); setErrors(p => ({ ...p, description: "" })); }}
                placeholder="Enter description..." rows={3}
                style={{ ...(readOnly ? pillTextareaReadonly : pillTextareaStyle), border: errors.description ? "1.5px solid #c00" : "none" }} />
              {!readOnly && (<p style={{ fontSize: "0.7rem", color: "#8a8a8a", textAlign: "right", margin: "4px 0 0" }}>{(form.description || "").length}/{POST_DESCRIPTION_MAX}</p>)}
              <FieldError msg={errors.description} />
              </div>

              <div id="cpostf-requirements">
              <FieldLabel>Requirements:</FieldLabel>
              <textarea className="ojt-field ojt-textarea" disabled={readOnly} value={form.requirements} maxLength={POST_LIST_FIELD_MAX}
                onChange={e => { set("requirements", e.target.value); setErrors(p => ({ ...p, requirements: "" })); }}
                placeholder="Enter requirements..." rows={2}
                style={{ ...(readOnly ? pillTextareaReadonly : pillTextareaStyle), border: errors.requirements ? "1.5px solid #c00" : "none" }} />
              {!readOnly && (<p style={{ fontSize: "0.7rem", color: "#8a8a8a", textAlign: "right", margin: "4px 0 0" }}>{(form.requirements || "").length}/{POST_LIST_FIELD_MAX}</p>)}
              <FieldError msg={errors.requirements} />
              </div>
            </div>

            {/* Location + Mapbox Map (fixed to Account Profile location — not editable here) */}
            <div id="cpostf-location" style={{ width: "100%" }}>
              <FieldLabel>Location:</FieldLabel>
              {fixedAddress ? (
                <>
                  <div style={{ ...pillInputReadonly, marginBottom: "8px", display: "flex", alignItems: "center", boxSizing: "border-box", gap: "8px" }}>
                    <span>📍 {fixedAddress}</span>
                    {profileLoc.isManual && (
                      <span style={{ background: inkDeep, color: "white", fontFamily: font.ui, fontSize: "0.65rem", padding: "2px 8px", borderRadius: "10px", whiteSpace: "nowrap" }}>
                        
                      </span>
                    )}
                  </div>
                  <MapboxLocationPicker
                    key={fixedAddress}
                    value={fixedAddress}
                    lat={profileLoc.lat}
                    lng={profileLoc.lng}
                    onChange={(loc) => set("postLocation", loc)}
                    readOnly={true}
                  />
                </>
              ) : (
                <div style={{ background: "#f0e0e0", border: `1px dashed ${darkRed}`, borderRadius: "14px", padding: "14px 16px", fontFamily: font.ui, fontSize: "0.82rem", color: darkRed }}>
                  No location set yet. Please set your company location first.
                </div>
              )}
              <p style={{ fontFamily: font.ui, fontSize: "0.7rem", color: "#888", margin: "4px 0 0" }}>
                This follows your company's location.
              </p>
            </div>
          </div>

          {/* Working Hours */}
          <div className="post-hours-slot-row">
            <div id="cpostf-hours" style={{ flex: 1, minWidth: 0 }}>
              <FieldLabel>Working Hours:</FieldLabel>
              {(form.workingHoursList || [""]).map((hours, idx) => (
                <div key={idx} style={{ marginBottom: "4px" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <WorkingHoursInput
                        value={hours}
                        onChange={v => {
                          const updated = [...form.workingHoursList];
                          updated[idx] = v;
                          set("workingHoursList", updated);
                          const newWh = [...workingHoursErrors];
                          newWh[idx] = "";
                          setWorkingHoursErrors(newWh);
                        }}
                        readOnly={readOnly}
                        hasError={!!workingHoursErrors[idx]}
                      />
                    </div>
                    {!readOnly && form.workingHoursList.length > 1 && (
                      <button type="button"
                        onClick={() => {
                          const u = [...form.workingHoursList]; u.splice(idx, 1); set("workingHoursList", u);
                          const we = [...workingHoursErrors]; we.splice(idx, 1); setWorkingHoursErrors(we);
                        }}
                        style={{ width: "28px", height: "28px", borderRadius: "50%", background: inkDeep, border: "none", color: "white", fontFamily: font.ui, fontSize: "0.85rem", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                    )}
                  </div>
                  {workingHoursErrors[idx] && <FieldError msg={workingHoursErrors[idx]} />}
                </div>
              ))}
              {!readOnly && (
                <button type="button" onClick={() => set("workingHoursList", [...form.workingHoursList, ""])}
                  style={{ background: "none", border: `1.5px dashed ${inkDeep}`, borderRadius: "20px", color: inkDeep, width: "100%", padding: "7px", fontFamily: font.ui, fontSize: "0.82rem", cursor: "pointer", fontWeight: 600 }}>
                  + Add Another Working Hours
                </button>
              )}
            </div>
          </div>

          {/* Post Expiration Date — optional. Once this date has passed,
              students can no longer apply to this post (enforced on the
              student-facing apply screen; this modal only lets the company
              set/edit the date and warns them once it's already passed). */}
          <div id="cpostf-expiration">
          <FieldLabel>Post Expiration Date:</FieldLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ maxWidth: "220px", flex: "1 1 180px" }}>
              <input
                type="date"
                className="ojt-field"
                disabled={readOnly}
                min={getTodayStr()}
                value={form.expirationDate}
                onChange={e => { set("expirationDate", e.target.value); setErrors(p => ({ ...p, expirationDate: "" })); }}
                style={{
                  ...(readOnly ? pillInputReadonly : pillInputStyle),
                  colorScheme: "light",
                  border: errors.expirationDate ? "1.5px solid #c00" : "none",
                }}
              />
            </div>
            {form.expirationDate && isPostExpired({ expirationDate: form.expirationDate }) && (
              <span style={{ background: red, color: "white", fontFamily: font.ui, fontWeight: 700, fontSize: "0.68rem", padding: "3px 10px", borderRadius: "10px", whiteSpace: "nowrap" }}>
                Expired
              </span>
            )}
          </div>
          <p style={{ fontFamily: font.ui, fontSize: "0.7rem", color: "#888", margin: "4px 0 0" }}>
            Optional. Leave blank if this post should stay open indefinitely. Once this date has passed, students can no longer apply.
          </p>
          <FieldError msg={errors.expirationDate} />
          </div>

          {/* Contact Information */}
          <div id="cpostf-contact">
          <FieldLabel>Contact Information:</FieldLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingLeft: "2px" }}>
            <div className="post-contact-row">
              <span style={{ ...inlineLabelStyle, paddingTop: "8px" }}>Phone Number:</span>
              <div style={{ flex: 1 }}>
                <PhoneInput value={form.phone} onChange={v => { set("phone", v); setErrors(p => ({ ...p, phone: validatePhone(v) })); }} readOnly={readOnly} hasError={!!errors.phone} />
                <FieldError msg={errors.phone} />
              </div>
            </div>
            <div className="post-contact-row">
              <span style={{ ...inlineLabelStyle, paddingTop: "8px" }}>Email:</span>
              <div style={{ flex: 1 }}>
                <GmailInput value={form.contactEmail}
                  onChange={v => { set("contactEmail", v); setErrors(p => ({ ...p, contactEmail: validateGmail(v) })); }}
                  onBlur={() => { const e = validateGmail(form.contactEmail); if (e) setErrors(p => ({ ...p, contactEmail: e })); }}
                  readOnly={readOnly} error={errors.contactEmail} />
              </div>
            </div>
          </div>

          </div>

          {/* Benefits */}
          <div id="cpostf-benefits">
          <FieldLabel>Benefits:</FieldLabel>
          <textarea className="ojt-field ojt-textarea" disabled={readOnly} value={form.benefits} maxLength={POST_LIST_FIELD_MAX}
            onChange={e => { set("benefits", e.target.value); setErrors(p => ({ ...p, benefits: "" })); }}
            placeholder="Enter benefits..." rows={2}
            style={{ ...(readOnly ? pillTextareaReadonly : pillTextareaStyle), border: errors.benefits ? "1.5px solid #c00" : "none" }} />
          {!readOnly && (<p style={{ fontSize: "0.7rem", color: "#8a8a8a", textAlign: "right", margin: "4px 0 0" }}>{(form.benefits || "").length}/{POST_LIST_FIELD_MAX}</p>)}
          <FieldError msg={errors.benefits} />
          </div>

          {/* Industry — fixed to Account Profile's industry, not editable here */}
          <div id="cpostf-industry">
          <FieldLabel>Industry:</FieldLabel>
          <div style={{ ...pillInputReadonly, marginBottom: "8px", boxSizing: "border-box" }}>
            {Array.isArray(companyProfile?.industry)
              ? (companyProfile.industry.join(", ") || "—")
              : (companyProfile?.industry || user?.industry || "—")}
          </div>
          </div>

          {/* College / Program required — restricted to approved departments,
              each with its own slot count */}
          <div id="cpostf-programs">
          <FieldLabel>College / Program required (set slots per department):</FieldLabel>
          {isEditing && mode !== "create" && droppedCourseLabels.length > 0 && (
            <div role="status" style={{
              background: "#FAF1DD", border: "1px solid #E4CE9B", borderRadius: "14px",
              padding: "12px 14px", marginBottom: "10px",
            }}>
              <p style={{ fontFamily: font.ui, fontSize: "0.78rem", color: "#7A5B10", margin: 0, lineHeight: 1.6 }}>
                {droppedCourseLabels.join(", ")} {droppedCourseLabels.length === 1 ? "is" : "are"} no longer in your accepted courses,
                so {droppedCourseLabels.length === 1 ? "it has" : "they have"} been removed from this post. Students who already applied keep their applications.
                To offer {droppedCourseLabels.length === 1 ? "it" : "them"} again, add {droppedCourseLabels.length === 1 ? "it" : "them"} back in Account Profile and wait for coordinator approval.
              </p>
            </div>
          )}
          <ApprovedDepartmentPicker
            approvedDeptSelections={approvedDeptSelections}
            selections={form.courseSelections}
            onChange={v => { set("courseSelections", v); setCourseErrors(""); }}
            readOnly={readOnly}
          />
          {courseErrors && <FieldError msg={courseErrors} />}
          </div>

          {/* Skills */}
          <div id="cpostf-skills">
          <FieldLabel>Skills Required:</FieldLabel>
          <textarea className="ojt-field ojt-textarea" disabled={readOnly} value={form.skillsRequired} maxLength={POST_LIST_FIELD_MAX}
            onChange={e => { set("skillsRequired", e.target.value); setErrors(p => ({ ...p, skillsRequired: "" })); }}
            placeholder="Enter required skills..." rows={2}
            style={{ ...(readOnly ? pillTextareaReadonly : pillTextareaStyle), border: errors.skillsRequired ? "1.5px solid #c00" : "none" }} />
          {!readOnly && (<p style={{ fontSize: "0.7rem", color: "#8a8a8a", textAlign: "right", margin: "4px 0 0" }}>{(form.skillsRequired || "").length}/{POST_LIST_FIELD_MAX}</p>)}
          <FieldError msg={errors.skillsRequired} />
          </div>
        </div>

        {/* Footer */}
        <div className="post-modal-footer" style={{ background: "#b0b0b0", display: "flex", justifyContent: "flex-end", gap: "10px", borderBottomLeftRadius: "20px", borderBottomRightRadius: "20px", flexShrink: 0 }}>
          <div id="cpostf-footer" style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={handleCloseClick} style={{ padding: "10px 28px", borderRadius: "24px", background: "#555", color: "white", border: "none", fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(0.85rem, 2.3vw, 0.95rem)", cursor: "pointer" }}>Close</button>
          {mode === "view" && !isEditing && (
            <button onClick={() => setIsEditing(true)} style={{ padding: "10px 28px", borderRadius: "24px", background: inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(0.85rem, 2.3vw, 0.95rem)", cursor: "pointer" }}>Edit</button>
          )}
          {isEditing && mode !== "create" && (
            <button onClick={handleSave} style={{ padding: "10px 28px", borderRadius: "24px", background: inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(0.85rem, 2.3vw, 0.95rem)", cursor: "pointer" }}>Save</button>
          )}
          {mode === "create" && (
            <button
              onClick={handleSave}
              disabled={approvedDeptSelections.length === 0}
              style={{
                padding: "10px 28px", borderRadius: "24px",
                background: approvedDeptSelections.length === 0 ? "#999" : inkDeep,
                color: "white", border: "none", fontFamily: font.ui, fontWeight: 600,
                fontSize: "clamp(0.85rem, 2.3vw, 0.95rem)",
                cursor: approvedDeptSelections.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Post
            </button>
          )}
          </div>
        </div>
      </div>

      {showDiscardConfirm && (
        <ConfirmDiscardModal
          onKeepEditing={() => setShowDiscardConfirm(false)}
          onDiscard={() => { setShowDiscardConfirm(false); onClose(); }}
        />
      )}

      {showSaved && (
        <SavedSuccessModal onClose={() => { setShowSaved(false); onClose(); }} />
      )}
    </div>
  );
};

// ── Three-dot Menu ────────────────────────────────────────────────────────────
const menuItemStyle = {
  display: "block", width: "100%", padding: "10px 16px",
  background: "none", border: "none", textAlign: "left",
  fontFamily: font.ui, fontSize: "0.9rem",
  cursor: "pointer", color: "#1a1a1a",
};

// `open` is controlled by the parent (one openMenuId for the whole list) so a
// second ⋮ closes the first — with per-menu state every post could be left
// hanging open at once. A pointerdown listener on the document closes it when
// anything else on the page is clicked.
// Which options show depends on the post's state:
//   expired            → Delete only (enabling/viewing a closed post is pointless;
//                        this wins even if the post is also disabled)
//   disabled (not expired) → Enable only
//   enabled            → View, Disable, Delete
const ThreeDotMenu = ({ isDisabled, isExpired, onView, onToggleDisable, onDelete, onEdit, open, onOpenChange }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onOpenChange(false);
    };
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  const setOpen = (next) => onOpenChange(next);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(!open); }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Post options"
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", color: "#555", padding: "4px 8px", lineHeight: 1 }}>⋮</button>
      {open && (
        <div
          style={{ position: "absolute", right: 0, top: "100%", background: "white", border: "1px solid #ddd", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 100, minWidth: "120px", overflow: "hidden" }}
          onClick={e => e.stopPropagation()}
        >
          {isExpired ? (
            <button onClick={() => { setOpen(false); onDelete(); }}         style={{ ...menuItemStyle, color: red, fontWeight: "700" }}>Delete</button>
          ) : isDisabled ? (
            <>
              <button onClick={() => { setOpen(false); onToggleDisable(); }}  style={menuItemStyle}>Enable</button>
              {/* A post closed because its programs were withdrawn needs an
                  approved program picked before it can be enabled again. */}
              {onEdit && <button onClick={() => { setOpen(false); onEdit(); }} style={menuItemStyle}>Edit</button>}
              <button onClick={() => { setOpen(false); onDelete(); }}         style={{ ...menuItemStyle, color: red, fontWeight: "700" }}>Delete</button>
            </>
          ) : (
            <>
              <button onClick={() => { setOpen(false); onView(); }}           style={menuItemStyle}>View</button>
              <button onClick={() => { setOpen(false); onToggleDisable(); }}  style={menuItemStyle}>Disable</button>
              <button onClick={() => { setOpen(false); onDelete(); }}         style={{ ...menuItemStyle, color: red, fontWeight: "700" }}>Delete</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Post OJT Content ──────────────────────────────────────────────────────────
const PostOJTContent = ({ user, openPostId, onPostOpened, onViewChange }) => {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [companyProfile, setCompanyProfile] = useState({});
  // Only one post menu may be open at a time; null = none.
  const [openMenuId, setOpenMenuId] = useState(null);

  // Pending Delete/Disable/Enable action awaiting the user's confirmation —
  // { type: "delete" | "disable" | "enable", post }.
  const [confirmAction, setConfirmAction] = useState(null);

  const openCreate = () => setModal({ mode: "create", post: null });
  const openView   = (post) => setModal({ mode: "view", post });
  const closeModal = () => setModal(null);

  // "list" | "create" | "view" | "edit" → Dashboard's "?" help button and
  // first-visit auto-tour. The modal reports its own mode (view ↔ edit).
  const [modalMode, setModalMode] = useState(null);
  const subView = modal ? (modalMode || (modal.mode === "create" ? "create" : "view")) : "list";
  useEffect(() => { onViewChange?.(subView); }, [subView]);
  useEffect(() => () => onViewChange?.("list"), []);
  useEffect(() => { if (!modal) setModalMode(null); }, [modal]);

  // Auto-open a specific post's details when navigated here with a target id
  // (e.g. clicking a "Recent Post" on the dashboard).
  useEffect(() => {
    if (!openPostId) return;
    const target = posts.find(p => p.id === openPostId);
    if (target) {
      // A disabled or expired post can't be viewed — clear the target without opening it.
      if (!target.disabled && !isPostExpired(target)) openView(target);
      onPostOpened?.();
    }
  }, [openPostId, posts, onPostOpened]);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "companies", user.uid), (snap) => {
      if (snap.exists()) setCompanyProfile(snap.data());
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "ojt_posts"), where("companyId", "==", user.uid));
    const unsub = onSnapshot(q, snap => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      loaded.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPosts(loaded);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [user?.uid]);

  // Live count of applications per post. A post that students have applied
  // to is ARCHIVED instead of deleted, so their Recent Applications, the
  // coordinator's Student List and the application history stay intact.
  const [appCountByPost, setAppCountByPost] = useState({});
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "applications"), where("companyId", "==", user.uid));
    const unsub = onSnapshot(q, snap => {
      const counts = {};
      snap.docs.forEach(d => {
        const pid = d.data().postId;
        if (pid) counts[pid] = (counts[pid] || 0) + 1;
      });
      setAppCountByPost(counts);
    }, err => console.error("Failed to load application counts:", err));
    return () => unsub();
  }, [user?.uid]);

  // Targets on a post that are still backed by an APPROVED affiliation.
  const approvedTargetsOf = (post) => {
    const approved = (companyProfile?.deptSelections || []).filter(s => s.status === "approved");
    const targets = Array.isArray(post.courseSelections) && post.courseSelections.length
      ? post.courseSelections
      : (post.departments || []).map(d => ({ college: d, program: "" }));
    return targets.filter(t => t?.college && isPairApproved(approved, t.college, t.program));
  };
  const hasAnyApprovedProgram = (companyProfile?.deptSelections || []).some(s => s.status === "approved");

  const activePosts   = posts.filter(p => !p.archived);
  const archivedPosts = posts.filter(p => p.archived);
  const [showArchived, setShowArchived] = useState(false);

  const handleSave = async (formData) => {
    if (!user?.uid) return;
    // Flat college-name array derived from courseSelections — lets
    // CoordinatorFindCompanyScreen/StudentFindCompanyScreen filter posts by
    // college without needing to read into the courseSelections sub-array.
    const departments = (formData.courseSelections || []).map(s => s.college).filter(Boolean);
    // Total across all selected departments — kept alongside the per-department
    // counts in courseSelections so screens that still read a single `slot`
    // number (student/coordinator views) keep working unchanged.
    const totalSlot = (formData.courseSelections || []).reduce((sum, s) => sum + (s.slot || 1), 0);
    if (modal.mode === "create") {
      await addDoc(collection(db, "ojt_posts"), {
        ...formData,
        departments,
        slot:        totalSlot,
        companyId:   user.uid,
        companyName: companyProfile.companyName || user.companyName || "",
        industry:    companyProfile.industry    || user.industry    || "",
        location:    companyProfile.location    || user.location    || {},
        disabled:    false,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "ojt_posts", modal.post.id), {
        ...formData,
        departments,
        slot:      totalSlot,
        industry: companyProfile.industry || user.industry || "",
        updatedAt: serverTimestamp(),
      });
      // If a previously withdrawn (and now re-approved) program was put back
      // on this post, the applications flagged by that withdrawal become
      // active again. Runs after the post save; a failure here never undoes it.
      try {
        await restoreApplicationsForPost({
          companyId:        user.uid,
          companyName:      companyProfile.companyName || user.companyName || "",
          postId:           modal.post.id,
          courseSelections: formData.courseSelections || [],
          deptSelections:   companyProfile.deptSelections || [],
        });
      } catch (err) {
        console.error("Post saved, but restoring earlier applications failed:", err);
      }
    }
  };

  const toggleDisable = async (id) => {
    const post = posts.find(p => p.id === id);
    if (!post) return;
    if (post.disabled) {
      // Re-check at write time — approvals can change while the dialog is open.
      if (approvedTargetsOf(post).length === 0) {
        setConfirmAction({ type: "blocked", post });
        return;
      }
      await updateDoc(doc(db, "ojt_posts", id), {
        disabled: false, autoDisabledReason: null, autoDisabledAt: null, updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, "ojt_posts", id), { disabled: true, updatedAt: serverTimestamp() });
    }
  };

  // Tells the people with a stake in a closed post, grouped so nobody gets
  // one notice per application:
  //   • each student with an OPEN or ACCEPTED application (declined and
  //     withdrawn ones have nothing left to happen, so they're skipped);
  //   • one informational notice per program for coordinators (no ⚑ — the
  //     applications stay active; nothing is lost).
  const OPEN_STATUSES = ["Pending", "In Review", "To Interview"];
  const notifyPostClosed = async (postId, apps) => {
    const companyName = companyProfile.companyName || user.companyName || "The company";
    const relevant = apps.filter(a => OPEN_STATUSES.includes(a.status || "Pending") || a.status === "Accepted");
    if (relevant.length === 0) return;
    const batch = writeBatch(db);

    const byStudent = new Map();
    relevant.forEach(a => {
      if (!a.studentId) return;
      const list = byStudent.get(a.studentId) || [];
      list.push(a);
      byStudent.set(a.studentId, list);
    });
    byStudent.forEach((list, studentId) => {
      const anyOpen = list.some(a => OPEN_STATUSES.includes(a.status || "Pending"));
      batch.set(doc(collection(db, "notifications")), {
        studentId,
        type:          "post_closed",
        companyId:     user.uid,
        companyName,
        postId,
        applicationId: list[0].id,
        message: anyOpen
          ? `${companyName} closed the post you applied to. Your application is still active and the company can still update your status.`
          : `${companyName} closed the post you were accepted for. Your placement and application stay on record.`,
        read:          false,
        createdAt:     serverTimestamp(),
      });
    });

    const byProgram = new Map();
    relevant.forEach(a => {
      const key = `${a.college || ""}||${a.program || ""}`;
      const entry = byProgram.get(key) || { college: a.college || "", program: a.program || "", students: new Set(), open: 0, accepted: 0 };
      if (a.studentId) entry.students.add(a.studentId);
      if (a.status === "Accepted") entry.accepted += 1; else entry.open += 1;
      byProgram.set(key, entry);
    });
    byProgram.forEach(e => {
      const n = e.students.size || (e.open + e.accepted);
      const parts = [];
      if (e.open) parts.push(`${e.open} still open`);
      if (e.accepted) parts.push(`${e.accepted} accepted`);
      batch.set(doc(collection(db, "notifications")), {
        recipientRole:    "coordinator",
        department:       e.college,
        program:          e.program,
        type:             "post_closed",
        companyId:        user.uid,
        companyName,
        postId,
        openApplications: 0,   // informational — no ⚑ follow-up
        message:          `${companyName} closed a post. ${n} of your student${n > 1 ? "s" : ""} applied there (${parts.join(", ")}); ${n > 1 ? "their applications stay" : "the application stays"} active with the company.`,
        readBy:           [],
        createdAt:        serverTimestamp(),
      });
    });

    await batch.commit();
  };

  const deletePost = async (id) => {
    // Re-count at confirm time instead of trusting the live counter alone.
    const snap = await getDocs(query(
      collection(db, "applications"),
      where("companyId", "==", user.uid),
      where("postId", "==", id),
    ));
    if (snap.size > 0) {
      await updateDoc(doc(db, "ojt_posts", id), {
        archived: true,
        disabled: true,           // keeps it out of both Find Company screens (they query disabled == false)
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      // Best-effort: the archive above is already saved.
      notifyPostClosed(id, snap.docs.map(d => ({ id: d.id, ...d.data() })))
        .catch(err => console.error("Post archived, but notifying students/coordinators failed:", err));
      return;
    }
    await deleteDoc(doc(db, "ojt_posts", id));
  };

  // Brings an archived post back into the active list. It comes back DISABLED,
  // not live: archiving told students and coordinators the post had closed, and
  // the company's approved courses may have changed since, so going live again
  // is a separate, deliberate Enable (which re-checks the approved courses).
  // The applications were never touched by archiving, so there's nothing to restore there.
  const restorePost = async (id) => {
    await updateDoc(doc(db, "ojt_posts", id), {
      archived:   false,
      archivedAt: null,
      disabled:   true,
      updatedAt:  serverTimestamp(),
    });
  };
  const requestRestore = (post) => setConfirmAction({ type: "restore", post });

  // Opens the confirm modal for a Delete/Disable/Enable request instead of
  // acting immediately — the actual write only happens once the user confirms.
  const requestToggleDisable = (post) => {
    if (post.disabled && approvedTargetsOf(post).length === 0) {
      setConfirmAction({ type: "blocked", post });
      return;
    }
    setConfirmAction({ type: post.disabled ? "enable" : "disable", post });
  };
  const requestDelete = (post) =>
    setConfirmAction({ type: (appCountByPost[post.id] || 0) > 0 ? "archive" : "delete", post });

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, post } = confirmAction;
    setConfirmAction(null);
    try {
      if (type === "blocked") {
        if (hasAnyApprovedProgram) openView(post);  // pick an approved program, save, then Enable
        return;
      }
      if (type === "delete" || type === "archive") await deletePost(post.id);
      else if (type === "restore") await restorePost(post.id);
      else await toggleDisable(post.id);
    } catch (err) {
      console.error(`Failed to ${type} post:`, err);
    }
  };

  return (
    <div className="post-screen-padding" style={{ overflowY: "auto", flex: 1 }}>
      <div style={{ background: "#e0e0e0", borderRadius: "16px", padding: "18px 20px", minHeight: "80vh" }}>

        <div className="post-header-row">
          <h2 style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(1.25rem, 3.6vw, 1.6rem)", letterSpacing: "-0.01em", margin: 0, color: "#1a1a1a" }}>
            Recent Post
          </h2>
          <button
            id="cpost-create-btn"
            className="post-btn"
            onClick={openCreate}
            style={{ background: inkDeep, color: "white", border: "none", borderRadius: "24px", fontFamily: font.ui, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
          >
            Post <span>+</span>
          </button>
        </div>

        <hr style={{ border: "none", borderTop: "2px solid #aaa", marginBottom: "16px" }} />

        {activePosts.length > 0 ? (
          <div id="cpost-list" className="post-grid">
            {activePosts.map(post => {
              const expired = isPostExpired(post);
              // Wait for the company profile before judging, so the badge doesn't flash on load.
              const noApprovedTargets = !!companyProfile?.deptSelections && approvedTargetsOf(post).length === 0;
              return (
                <div
                  key={post.id}
                  // A disabled or expired post can't be opened — not from the ⋮
                  // menu (which drops View for both) and not by clicking the
                  // card either, so the two can't disagree.
                  onClick={() => { if (!post.disabled && !expired) openView(post); }}
                  style={{
                    background: post.disabled ? "#b8b8b8" : "white",
                    borderRadius: "14px", padding: "14px 16px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    cursor: post.disabled || expired ? "default" : "pointer",
                    boxShadow: post.disabled ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
                    opacity: post.disabled ? 0.75 : (expired ? 0.85 : 1),
                    border: post.disabled ? "none" : (expired ? `1.5px solid ${red}` : "1.5px solid #e8e8e8"),
                    minWidth: 0,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <p style={{
                        fontFamily: font.ui, fontWeight: 600,
                        fontSize: "clamp(0.95rem, 2.5vw, 1.0625rem)",
                        margin: "0 0 4px", color: post.disabled ? "#666" : "#1a1a1a",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {post.companyName || post.company || "Unnamed Company"}
                      </p>
                      {!post.disabled && expired && (
                        <span style={{ background: red, color: "white", fontFamily: font.ui, fontWeight: 700, fontSize: "0.62rem", padding: "2px 8px", borderRadius: "10px", whiteSpace: "nowrap", marginBottom: "4px" }}>
                          Expired
                        </span>
                      )}
                      {post.disabled && noApprovedTargets && (
                        <span style={{ background: "#6b6b6b", color: "white", fontFamily: font.ui, fontWeight: 700, fontSize: "0.62rem", padding: "2px 8px", borderRadius: "10px", whiteSpace: "nowrap", marginBottom: "4px" }}>
                          No approved course
                        </span>
                      )}
                    </div>
                    <p style={{ fontFamily: font.ui, fontSize: "0.78rem", margin: 0, color: "#888" }}>
                      {post.industry || post.subtitle || (post.courseSelections?.[0] ? post.courseSelections[0].college : "OJT Post")}
                    </p>
                  </div>
                  <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                    <ThreeDotMenu
                      isDisabled={post.disabled}
                      isExpired={expired}
                      open={openMenuId === post.id}
                      onOpenChange={(next) => setOpenMenuId(next ? post.id : null)}
                      onView={() => openView(post)}
                      onToggleDisable={() => requestToggleDisable(post)}
                      onDelete={() => requestDelete(post)}
                      onEdit={post.disabled && noApprovedTargets && hasAnyApprovedProgram ? () => openView(post) : undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div id="cpost-list" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80px" }}>
            <p style={{ fontFamily: font.ui, fontSize: "1rem", color: "#aaa" }}>No posts yet.</p>
          </div>
        )}

        {/* Archived = removed by the company after students had applied. Can be restored
            (comes back disabled); its applicants are still managed from the Applicants screen. */}
        {archivedPosts.length > 0 && (
          <div style={{ marginTop: "18px" }}>
            <button
              onClick={() => setShowArchived(v => !v)}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: font.ui, fontWeight: 700, fontSize: "0.85rem", color: "#555" }}
            >
              {showArchived ? "▾" : "▸"} Archived posts ({archivedPosts.length})
            </button>
            {showArchived && (
              <div className="post-grid" style={{ marginTop: "10px" }}>
                {archivedPosts.map(post => (
                  <div key={post.id} style={{ background: "#cfcfcf", borderRadius: "14px", padding: "14px 16px", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                    <div style={{ opacity: 0.8, minWidth: 0, flex: 1 }}>
                    <p style={{ fontFamily: font.ui, fontWeight: 600, fontSize: "clamp(0.95rem, 2.5vw, 1.0625rem)", margin: "0 0 4px", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {post.companyName || post.company || "Unnamed Company"}
                    </p>
                    <p style={{ fontFamily: font.ui, fontSize: "0.75rem", margin: 0, color: "#666" }}>
                      {(post.courseSelections || []).map(s => [s.college, s.program].filter(Boolean).join(" — ")).join(", ") || post.industry || "OJT Post"}
                      {" · "}{appCountByPost[post.id] || 0} application{(appCountByPost[post.id] || 0) === 1 ? "" : "s"} kept
                    </p>
                    </div>
                    {isPostExpired(post) ? (
                      // An expired post can't be opened, so its date could never be extended --
                      // restoring it would only bring back something that is closed anyway.
                      <span style={{ fontFamily: font.ui, fontSize: "0.72rem", color: "#777", flexShrink: 0, textAlign: "right" }}>Expired</span>
                    ) : (
                      <button
                        onClick={() => requestRestore(post)}
                        style={{ flexShrink: 0, padding: "7px 16px", borderRadius: "20px", background: inkDeep, color: "white", border: "none", fontFamily: font.ui, fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <PostFormModal
          post={modal.post}
          mode={modal.mode}
          onClose={closeModal}
          onSave={handleSave}
          user={user}
          companyProfile={companyProfile}
          onModeChange={setModalMode}
        />
      )}

      {confirmAction && (
        <ConfirmActionModal
          title={
            confirmAction.type === "delete"  ? "Delete this post?" :
            confirmAction.type === "archive" ? "Archive this post?" :
            confirmAction.type === "disable" ? "Disable this post?" :
            confirmAction.type === "blocked" ? "Can't enable this post yet" :
            confirmAction.type === "restore" ? "Restore this post?" :
                                                "Enable this post?"
          }
          message={
            confirmAction.type === "delete"
              ? "This post will be permanently removed and can't be recovered. Are you sure you want to delete it?"
              : confirmAction.type === "archive"
              ? `${appCountByPost[confirmAction.post.id] || 0} student${(appCountByPost[confirmAction.post.id] || 0) === 1 ? " has" : "s have"} applied to this post, so it will be archived instead of deleted. It disappears from Find Company and stops accepting applications, but the existing applications and their history are kept. This can't be undone.`
              : confirmAction.type === "restore"
              ? "This post goes back to your list as disabled, so students still can't see it. Enable it when you're ready for it to go live again. Its applications were never removed."
              : confirmAction.type === "disable"
              ? "Students won't be able to see or apply to this post while it's disabled. You can enable it again anytime."
              : confirmAction.type === "blocked"
              ? (hasAnyApprovedProgram
                  ? "None of the courses on this post are approved for your company anymore. Edit the post, choose at least one approved course, save it, then enable it again."
                  : "Your company has no approved course right now. Once a coordinator approves one of your courses in Account Profile, you can add it to this post and enable it.")
              : `This post will become visible again to students of: ${approvedTargetsOf(confirmAction.post).map(t => [t.college, t.program].filter(Boolean).join(" — ")).join(", ")}. Continue?`
          }
          confirmLabel={
            confirmAction.type === "delete"  ? "Delete"  :
            confirmAction.type === "archive" ? "Archive" :
            confirmAction.type === "disable" ? "Disable" :
            confirmAction.type === "blocked" ? (hasAnyApprovedProgram ? "Edit post" : "OK") :
            confirmAction.type === "restore" ? "Restore" :
                                                "Enable"
          }
          hideCancel={confirmAction.type === "blocked" && !hasAnyApprovedProgram}
          danger={confirmAction.type === "delete" || confirmAction.type === "archive"}
          onCancel={() => setConfirmAction(null)}
          onConfirm={runConfirmedAction}
        />
      )}
    </div>
  );
};

// ── Main Screen ───────────────────────────────────────────────────────────────
// Input ceilings for a post's long fields.
const POST_DESCRIPTION_MAX = 5000;
const POST_LIST_FIELD_MAX  = 1500;

const CompanyCreatePostScreen = ({ embedded = false, user, openPostId, onPostOpened, onViewChange }) => (
  <>
    <GlobalFonts />
    <ResponsiveStyles />
    {embedded
      ? <PostOJTContent user={user} openPostId={openPostId} onPostOpened={onPostOpened} onViewChange={onViewChange} />
      : (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f0f0f0" }}>
          <PostOJTContent user={user} openPostId={openPostId} onPostOpened={onPostOpened} onViewChange={onViewChange} />
        </div>
      )
    }
  </>
);

export default CompanyCreatePostScreen;